import styled from '@emotion/styled';

import Icon from 'components/global/Icon';

import { Breakpoints } from 'utils/const.styles';

export const Container = styled.div`
	display: flex;
	flex-direction: column;
	background-color: var(--white);
	border: 1px solid var(--border-color);
	border-radius: var(--small-border-radius);

	width: 100%;
	height: max-content;
	overflow: auto;
`;

export const TableContainer = styled.div`
	padding: 0 var(--padding-2);
	width: 100%;
	min-height: 10.3rem;
	overflow: auto;

	@media ${Breakpoints.mobile} {
		padding: 0;
	}

	body[dir='rtl'] & {
		min-height: 12.3rem;
	}
`;

export const TableWrapper = styled.div`
	width: 100%;
	height: 100%;
	overflow: auto;

	&.noScroll {
		overflow: hidden;
	}
`;

export const Table = styled.div`
	display: table;
	width: 100%;
	border-collapse: separate;
	border-spacing: 0px;
	color: var(--black);

	.thead {
		width: 100%;
		border-collapse: separate;
		border-spacing: 0px;
		background: var(--background-3);
		position: sticky;
		top: 0;
		z-index: var(--z-index-1);

		> div {
			height: 100%;
			padding: var(--padding-4) var(--padding-2);

			@media ${Breakpoints.mobile} {
				padding: var(--padding-2) 0;
			}
		}

		.tooltip {
			width: 100%;
		}

		.header-filter {
			padding: 0;
			width: 100%;

			.table-string-input,
			.table-number-input,
			.table-float-input,
			.enumselect-label,
			.multiselect-label,
			.flatpickr .alt-input {
				max-height: 3.5rem;
				min-height: 3.5rem;
				height: 100%;
				padding: 0.45rem 0.5rem;

				body[dir='rtl'] & {
					max-height: 4.5rem;
					min-height: 4.5rem;
					padding: 0.75rem 0.5rem;
				}
			}

			.alt-input {
				padding: 0.75rem 0.5rem 0.75rem 3rem !important;

				body[dir='rtl'] & {
					padding: 0.75rem 3rem 0.75rem 0.5rem !important;
				}
			}
		}
	}
	.tfoot {
		width: 100%;
		border-collapse: separate;
		border-spacing: 0px;
		position: sticky;
		bottom: 0;
		z-index: var(--z-index-1);
		background: var(--white);

		> div {
			height: 100%;
			padding: var(--padding-1) var(--padding-2);
			border-bottom: none;

			@media ${Breakpoints.mobile} {
				padding: var(--padding-2) 0;
			}
		}

		@media ${Breakpoints.mobile} {
			display: none;
		}
	}
	.tbody {
		width: 100%;
		border-collapse: separate;
		border-spacing: 0px;
	}

	.th {
		display: flex;
		flex-direction: column;
		justify-content: space-between;

		svg {
			margin-right: 0.3rem;

			body[dir='rtl'] & {
				margin-right: unset;
				margin-left: 0.3rem;
			}
		}

		color: var(--black);

		> .typography {
			flex: 1;
			font-weight: 500;
		}

		gap: 1rem;

		.table-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
		}
	}

	.td {
		overflow: hidden;
		text-overflow: ellipsis;

		&.row-actions {
			display: flex;
			justify-content: center;
			align-items: center;
			height: 100%;
			position: sticky;
			right: -2px;

			body[dir='rtl'] & {
				right: 0;
				left: -2px;
			}
		}

		:not(&.row-actions, .loading, .expanded) {
			padding: calc(var(--tableRowHeight) / 2) 0;

			@media ${Breakpoints.mobile} {
				padding: 0.5rem;
			}
		}

		&.expandable {
			display: flex;
			justify-content: space-between;
			align-items: center;
		}

		&.expanded {
			display: grid;
			grid-template-columns: 1fr 1fr;
			width: 100%;
			padding: 2rem;
			gap: 2rem;
		}
	}

	.th:hover + .resizer {
		opacity: 1;
	}

	.resizer {
		display: inline-block;
		width: 0.3rem;
		height: 2rem;
		opacity: 0;
		background-color: var(--black-20);
		z-index: var(--z-index-1);
		touch-action: none;
		margin-right: var(--margin-1);

		body[dir='rtl'] & {
			margin-right: unset;
			margin-left: var(--margin-1);
		}

		&.isResizing {
			background: var(--content-4);
		}

		:hover {
			opacity: 1;
		}

		@media ${Breakpoints.mobile} {
			opacity: 1;
		}
	}

	.center {
		display: flex;
		justify-content: center;
		align-items: center;
		flex: 1;
	}
`;

export const ThFilterContainer = styled.div`
	display: flex;
	padding-right: var(--padding-2);

	> input {
		width: 100%;
	}

	> .enumSelect {
		width: 100%;
	}

	body[dir='rtl'] & {
		padding-right: unset;
		padding-left: var(--padding-2);
	}
`;

export const ResizerSpace = styled.div`
	width: 0.8rem;
	padding: calc(var(--tableRowHeight) / 2) 0;

	@media ${Breakpoints.mobile} {
		padding: 0.5rem;
	}
`;

export const TableRow = styled.div`
	height: var(--tableRowHeight);
	justify-content: flex-start;
	align-items: center;
	padding: 0 var(--padding-2);
	overflow: hidden;
	border-bottom: 1px solid var(--border-color);

	> .row-actions {
		background-color: ${({ selected, selectedContextMenuRow }) =>
			selectedContextMenuRow
				? 'var(--table-active-color)'
				: selected
					? 'var(--table-selected-color)'
					: 'var(--white)'};
	}

	&.tr {
		background-color: ${({ selected, selectedContextMenuRow }) =>
			selectedContextMenuRow
				? 'var(--table-active-color)'
				: selected
					? 'var(--table-selected-color)'
					: 'transparent'};

		:hover,
		:focus-within {
			background-color: ${({ selectedContextMenuRow }) =>
				selectedContextMenuRow ? 'var(--table-active-color)' : 'var(--table-selected-color)'};

			> .row-actions {
				background-color: ${({ selectedContextMenuRow }) =>
					selectedContextMenuRow ? 'var(--table-active-color)' : 'var(--table-selected-color)'};
			}
		}
	}

	&.expanded {
		height: auto;
	}

	> .loading {
		padding-right: var(--padding-3);

		body[dir='rtl'] & {
			padding-right: unset;
			padding-left: var(--padding-3);
		}
	}

	@media ${Breakpoints.mobile} {
		padding: 0;
		min-width: unset !important;
		border-bottom: 1px solid var(--border-color);
		${({ selectionMode }) => !selectionMode && 'padding-left: 1rem !important;'}

		&.expanded {
			padding: 0 !important;
		}

		body[dir='rtl'] & {
			${({ selectionMode }) => !selectionMode && 'padding-left: unset !important;'}
			${({ selectionMode }) => !selectionMode && 'padding-right: 1rem !important;'}
		}
	}

	&.column-calculation-row {
		cursor: pointer;

		:hover {
			background-color: var(--table-selected-color);

			.column-summary-calculate-button {
				display: flex !important;
			}
		}
	}
`;

export const Footer = styled.div`
	display: flex;
	align-items: center;
`;

export const HeaderContainer = styled.div`
	display: flex;
	flex-direction: row;
	gap: 0.5rem;
	overflow: auto;

	&.expanded {
		> .typography {
			font-weight: 500;
		}
	}
`;

export const ExpandedColumnContainer = styled.div`
	width: 100%;
	overflow: auto;
`;

export const InfiniteScrollPaginationContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	flex: 1;
	gap: 0.5rem;
	padding: var(--padding-2) var(--padding-4);
`;

export const InfiniteScrollLoadingContainer = styled.div`
	display: flex;
	flex: 1;
	padding: 0 var(--padding-2);
`;

export const NoMoreInfiniteScrollRecordsContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	flex: 1;
`;

export const ColumnSummaryContainer = styled.div`
	display: flex;
	height: 4rem;

	> .button {
		display: none;

		> .icon {
			transition: all 0.3s ease-in-out;
		}

		&.popper_open {
			display: flex;

			> .icon {
				transform: rotate(180deg);
			}
		}
	}
`;

export const ColumnSummaryBox = styled.div`
	display: flex;
	height: 4rem;
	padding: 1rem 3rem 1rem 1rem;
	margin-right: 1rem;
	width: 100%;
	align-items: center;
	border: 1px solid var(--border-color);
	border-radius: var(--spacing-2);
	position: relative;
	gap: 1rem;

	body[dir='rtl'] & {
		margin-right: unset;
		margin-left: 1rem;
		padding: 1rem 1rem 1rem 3rem;
	}
`;

export const ColumnSummaryBoxIcon = styled(Icon)`
	position: absolute;
	right: 0.5rem;

	body[dir='rtl'] & {
		left: 0.5rem;
		right: unset;
		transform: scaleX(-1);
	}
`;
